import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FeedComponent } from 'features/feed/FeedComponent';
import { LoginBadgeBackdrop } from 'shared/components/LoginBadgeBackdrop';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { getProfileFeedUpdates } from 'features/feed/useFeedUpdates';
import { useKeyboardNavigation } from 'shared/hooks/useKeyboardNavigation';
import { Profile } from 'features/profiles/useProfiles';
import { BadgeModes } from 'types/badge-modes';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { contributionsPath, isBattleMode, parseContributionsTab } from 'shared/lib/nav-paths';
import {
  getProfileSearchSort,
  profileIdsForSearchSort,
  subscribeProfileSearchSort,
} from 'features/profiles/profile-search-sort';
import { BadgeGalleryView } from 'features/gallery/BadgeGalleryView';
import { BadgeHashesView } from 'features/stats/BadgeHashesView';
import { BadgesBattleView, BattleViewportGate } from 'features/battle/views/BadgesBattleView';
import { BattleArenaView } from 'features/battle/views/BattleArenaView.tsx';
import { BattleHowToView } from 'features/battle/views/BattleHowToView.tsx';
import { BattleLeaderboardsView } from 'features/battle/views/BattleLeaderboardsView.tsx';
import { ClaimBadgesView } from 'features/claim/ClaimBadgesView';
import { ExploreProfilesView } from 'features/explore/ExploreProfilesView';
import { JourneysGalleryView } from 'features/gallery/JourneysGalleryView';
import { LeaderboardView } from 'features/profiles/LeaderboardView';
import { ProfileCollectedView } from 'features/profiles/ProfileCollectedView';
import { StatsView } from 'features/stats/StatsView';
import './AppShell.css';
import { ShellNavId, Sidebar } from 'shared/layout/Sidebar';

/** Let the scrolling badge field settle briefly before chrome/content fade in. */
const SIDEBAR_REVEAL_DELAY_MS = 140;
const CONTENT_REVEAL_EXTRA_MS = 100;
const REVEAL_DELAY_REDUCED_MS = 0;

interface AppShellProps {
  mode: BadgeModes;
  profile: Profile | null;
  /** Re-fetch sheet profiles/submissions (rebuilds feed). */
  onRefreshFeed?: () => void | Promise<void>;
  isRefreshingFeed?: boolean;
  avatarUrl?: string;
  submissionsCount: number;
  selectedProfileID: string | null;
  onNavigate: (nav: ShellNavId) => void;
  onClaimNewBadges: () => void;
  onSubmitBadgeIdea: () => void;
  onSignOut: () => void;
  onDevMockProfile?: () => void;
  devMockProfileID?: string | null;
  /** Dev: pretend you have a brand-new empty profile (0 badges / journeys). */
  devEmptyProfile?: boolean;
  /** Feed search / ladder focus — drives Dev Edit impersonation. */
  feedFocusId?: string | null;
  onFeedFocusChange?: (profileId: string | null) => void;
  setSelectedProfileID: (id: string | null) => void;
  setMode: (mode: BadgeModes) => void;
  setSelectedBadge?: (badge: Badge | null) => void;
  onSubmissionTriggered: (code: string) => void;
  onSaveDisabledChange: (disabled: boolean) => void;
  navigate: (path: string) => void;
  claimBadgePrime?: { id: string } | null;
  onClaimBadgePrimeConsumed?: () => void;
  /** Hold sidebar/content until boot data is ready — backdrop can scroll underneath. */
  deferChrome?: boolean;
  /** Notifies when the scrolling field has finished its enter animation. */
  onBackdropFieldReady?: (ready: boolean) => void;
}

const modeToNav = (mode: BadgeModes, selectedProfileID: string | null, ownProfileID: string | null): ShellNavId => {
  // Viewing someone else's profile (including when you have no profile yet / create flow).
  const viewingOther =
    selectedProfileID != null &&
    selectedProfileID !== ownProfileID &&
    (mode === 'collected' || mode === 'submitted' || mode === 'journeys' || mode === 'profile_feed');
  if (viewingOther || mode === 'explore') return 'explore';

  // Profile cleared but mode not updated yet — never flash My Badges.
  if (
    selectedProfileID == null &&
    (mode === 'collected' || mode === 'submitted' || mode === 'journeys' || mode === 'profile_feed')
  ) {
    return 'feed';
  }

  if (mode === 'editing_profile') return 'claim';
  if (mode === 'collected') return 'collected';
  if (mode === 'journeys') return 'journeys';
  if (mode === 'profile_feed') return 'profile_feed';
  if (mode === 'submitted') return 'submitted';
  if (mode === 'badge_gallery') return 'badge_gallery';
  if (mode === 'journey_gallery') return 'journey_gallery';
  if (mode === 'stats') return 'stats';
  if (mode === 'badge_hashes') return 'badge_hashes';
  if (mode === 'leaderboard') return 'leaderboard';
  if (mode === 'battle_how_to') return 'battle_how_to';
  if (mode === 'battle_arena') return 'battle_arena';
  if (mode === 'battle_leaderboards') return 'battle_leaderboards';
  if (mode === 'badges_battle') return 'badges_battle';
  return 'feed';
};

export const AppShell: React.FC<AppShellProps> = ({
  mode,
  profile,
  onRefreshFeed,
  isRefreshingFeed = false,
  avatarUrl,
  submissionsCount,
  selectedProfileID,
  onNavigate,
  onClaimNewBadges,
  onSubmitBadgeIdea,
  onSignOut,
  onDevMockProfile,
  devMockProfileID = null,
  devEmptyProfile = false,
  feedFocusId = null,
  onFeedFocusChange,
  setSelectedProfileID,
  setMode,
  setSelectedBadge,
  onSubmissionTriggered,
  onSaveDisabledChange,
  navigate,
  claimBadgePrime = null,
  onClaimBadgePrimeConsumed,
  deferChrome = false,
  onBackdropFieldReady,
}) => {
  const { profilesMap, rankedProfiles, badgesMap, feedUpdates } = useTalosData();
  const [sidebarReady, setSidebarReady] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  /** Boot fade runs once; later route swaps should not replay opacity 0 → 1. */
  const [contentEntering, setContentEntering] = useState(false);
  const [backdropHighlightIds, setBackdropHighlightIds] = useState<ReadonlySet<string> | null>(null);
  const [fieldReady, setFieldReady] = useState(false);

  const handleBackdropHighlight = useCallback((badgeIds: string[] | null) => {
    setBackdropHighlightIds(badgeIds && badgeIds.length > 0 ? new Set(badgeIds) : null);
  }, []);

  // Sticky: zoom/resize rebuilds the field and briefly reports not-ready — don't
  // tear down chrome or re-open the boot overlay after the first ready.
  const handleFieldReady = useCallback(
    (ready: boolean) => {
      if (!ready) return;
      setFieldReady(true);
      onBackdropFieldReady?.(true);
    },
    [onBackdropFieldReady]
  );

  useEffect(() => {
    if (deferChrome || !fieldReady) {
      setSidebarReady(false);
      setContentReady(false);
      setContentEntering(false);
      return;
    }
    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sidebarDelay = reduced ? REVEAL_DELAY_REDUCED_MS : SIDEBAR_REVEAL_DELAY_MS;
    const contentDelay = reduced ? REVEAL_DELAY_REDUCED_MS : SIDEBAR_REVEAL_DELAY_MS + CONTENT_REVEAL_EXTRA_MS;
    const sidebarTimer = window.setTimeout(() => setSidebarReady(true), sidebarDelay);
    const contentTimer = window.setTimeout(() => {
      setContentReady(true);
      setContentEntering(true);
    }, contentDelay);
    return () => {
      window.clearTimeout(sidebarTimer);
      window.clearTimeout(contentTimer);
    };
  }, [deferChrome, fieldReady]);

  useEffect(() => {
    if (!contentEntering) return;
    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const done = window.setTimeout(() => setContentEntering(false), reduced ? 220 : 600);
    return () => window.clearTimeout(done);
  }, [contentEntering]);

  const ownProfileID = profile ? getFirstPartOfEmail(profile.email) : null;
  const ownFeedCount = useMemo(() => {
    if (!profile) return 0;
    // Sidebar omits idea-credit catalog cards (those only show on the profile Feed tab).
    return getProfileFeedUpdates(feedUpdates, profile, badgesMap).filter((u) => !u.isCatalogAddition).length;
  }, [feedUpdates, profile, badgesMap]);
  const isFeed = mode === 'splash';
  const isLeaderboard = mode === 'leaderboard';
  const isExplore = mode === 'explore';
  const isGallery = mode === 'badge_gallery';
  const isJourneyGallery = mode === 'journey_gallery';
  const isClaiming = mode === 'editing_profile';
  const isStats = mode === 'stats';
  const isBadgeHashes = mode === 'badge_hashes';
  const isBattle = isBattleMode(mode);
  const isBattleHowTo = mode === 'battle_how_to';
  const isBattleArena = mode === 'battle_arena';
  const isBattleLeaderboards = mode === 'battle_leaderboards';
  const isBattleClassic = mode === 'badges_battle';
  const selectedProfile = selectedProfileID ? (profilesMap.get(selectedProfileID) ?? null) : null;
  const isProfileGallery =
    selectedProfile != null &&
    (mode === 'collected' || mode === 'submitted' || mode === 'journeys' || mode === 'profile_feed');
  const showLightMain =
    isFeed ||
    isLeaderboard ||
    isExplore ||
    isGallery ||
    isJourneyGallery ||
    isProfileGallery ||
    isClaiming ||
    isStats ||
    isBadgeHashes ||
    isBattle ||
    isBattleHowTo;

  // Collected dig-down owns a sticky claimed-badge highlight.
  useEffect(() => {
    if (mode !== 'collected') return;
    setBackdropHighlightIds(selectedProfile?.badgesIDs.length ? new Set(selectedProfile.badgesIDs) : null);
  }, [mode, selectedProfile]);

  // Leaving collected: drop sticky highlights.
  // Leaderboard expand re-applies via onBackdropHighlight.
  useEffect(() => {
    if (mode === 'collected') return;
    setBackdropHighlightIds(null);
  }, [mode]);

  const [profileSort, setProfileSort] = useState(() => getProfileSearchSort());
  useEffect(() => subscribeProfileSearchSort(() => setProfileSort(getProfileSearchSort())), []);

  const rankedProfileOptions = useMemo(() => {
    const orderedIds = profileIdsForSearchSort(profilesMap.values(), profileSort, rankedProfiles);
    return orderedIds.flatMap((id) => {
      const p = profilesMap.get(id);
      if (!p) return [];
      return [
        {
          value: id,
          label: p.name,
          score: p.sortScore,
          lastUpdated: p.lastActivityAt,
          topEmoji: p.journeyEmojis.at(0) ?? '',
        },
      ];
    });
  }, [profilesMap, profileSort, rankedProfiles]);

  const openProfile = useCallback(
    (id: string) => {
      onFeedFocusChange?.(id);
      setSelectedProfileID(id);
      navigate(`/${id}`);
    },
    [navigate, onFeedFocusChange, setSelectedProfileID]
  );

  const openProfileFromStats = useCallback(
    (
      id: string,
      target?: {
        section: 'collected' | 'submitted' | 'journeys';
        groupSlug?: string;
      }
    ) => {
      onFeedFocusChange?.(id);
      setSelectedProfileID(id);
      if (target?.section === 'submitted') {
        navigate(contributionsPath(id));
        return;
      }
      if (target?.section === 'journeys') {
        navigate(`/${id}/journeys`);
        return;
      }
      navigate(target?.groupSlug ? `/${id}#${target.groupSlug}` : `/${id}`);
    },
    [navigate, onFeedFocusChange, setSelectedProfileID]
  );

  const openProfileJourneys = useCallback(
    (id: string) => {
      onFeedFocusChange?.(id);
      setSelectedProfileID(id);
      navigate(`/${id}/journeys`);
    },
    [navigate, onFeedFocusChange, setSelectedProfileID]
  );

  const openProfileKeepingSection = useCallback(
    (id: string) => {
      if (mode === 'journeys') {
        openProfileJourneys(id);
        return;
      }
      onFeedFocusChange?.(id);
      setSelectedProfileID(id);
      if (mode === 'submitted') navigate(contributionsPath(id, parseContributionsTab(window.location.hash)));
      else if (mode === 'profile_feed') navigate(`/${id}/feed`);
      else navigate(`/${id}`);
    },
    [mode, navigate, onFeedFocusChange, openProfileJourneys, setSelectedProfileID]
  );

  const handleRankedProfileChange = useCallback(
    (option: { value: string | null } | null) => {
      const id = option?.value ?? null;
      if (id == null) return;
      // Keep Collected / Contributions / Journeys / Feed when arrowing between profiles.
      if (mode === 'collected' || mode === 'submitted' || mode === 'journeys' || mode === 'profile_feed') {
        openProfileKeepingSection(id);
        return;
      }
      openProfile(id);
    },
    [mode, openProfile, openProfileKeepingSection]
  );

  useKeyboardNavigation({
    menuIsOpen: false,
    mode,
    setMode,
    selectedProfileID,
    options: rankedProfileOptions,
    handleOnChange: handleRankedProfileChange,
    enabled:
      mode !== 'stats' &&
      mode !== 'badge_hashes' &&
      mode !== 'editing_profile' &&
      mode !== 'splash' &&
      mode !== 'leaderboard' &&
      !isBattleMode(mode),
  });

  const clearSharedProfileFocus = useCallback(() => {
    onFeedFocusChange?.(null);
  }, [onFeedFocusChange]);

  const clearToFeed = useCallback(() => {
    onNavigate('feed');
  }, [onNavigate]);

  const clearProfileSelection = useCallback(() => {
    if (mode === 'journeys') {
      onNavigate('journey_gallery');
      return;
    }
    if (mode === 'collected') {
      onNavigate('badge_gallery');
      return;
    }
    clearToFeed();
  }, [mode, onNavigate, clearToFeed]);

  const mockedName = devMockProfileID ? (profilesMap.get(devMockProfileID)?.name ?? devMockProfileID) : null;

  return (
    <div
      className={`app-shell${sidebarReady ? ' app-shell--sidebar-ready' : ' app-shell--entering'}${
        contentReady ? ' app-shell--content-ready' : ''
      }${contentEntering ? ' app-shell--content-entering' : ''}`}
    >
      {process.env.NODE_ENV === 'development' && onDevMockProfile ? (
        <div className="dev-edit-stack">
          <button
            type="button"
            className={`dev-edit-button${devMockProfileID || devEmptyProfile ? ' active' : ''}`}
            onClick={onDevMockProfile}
            title={
              selectedProfileID || feedFocusId
                ? 'Impersonate the focused/selected profile (toggle)'
                : 'Preview the app with no profile (hidden from search & leaderboard)'
            }
          >
            {mockedName ? `Dev: ${mockedName}` : devEmptyProfile ? 'Dev: No Profile' : 'Dev Edit'}
          </button>
        </div>
      ) : null}

      <LoginBadgeBackdrop
        enableFlash={false}
        className="login-badge-backdrop--app app-shell-backdrop"
        highlightBadgeIds={backdropHighlightIds}
        onFieldReady={handleFieldReady}
      />

      {sidebarReady ? (
        <Sidebar
          profile={profile}
          avatarUrl={avatarUrl}
          activeNav={modeToNav(mode, selectedProfileID, ownProfileID)}
          submissionsCount={submissionsCount}
          feedCount={ownFeedCount}
          overlayRail={isBattle}
          onNavigate={onNavigate}
          onClaimNewBadges={onClaimNewBadges}
          onSubmitBadgeIdea={onSubmitBadgeIdea}
          onSignOut={onSignOut}
        />
      ) : (
        <div className="app-shell-sidebar-slot" aria-hidden />
      )}

      <div className="app-shell-content">
        <main className={`app-shell-main ${showLightMain ? 'feed-main' : 'embedded-main'}`}>
          {contentReady ? (
            isFeed ? (
              <FeedComponent
                currentProfile={profile}
                feedFocusId={feedFocusId}
                onFeedFocusChange={onFeedFocusChange}
                setSelectedProfileID={setSelectedProfileID}
                setSelectedBadge={setSelectedBadge}
                onRefresh={onRefreshFeed}
                isRefreshing={isRefreshingFeed}
              />
            ) : isLeaderboard ? (
              <LeaderboardView
                ownProfileId={ownProfileID}
                onSelectProfile={openProfile}
                onClearProfile={clearToFeed}
                onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                onBackdropHighlight={handleBackdropHighlight}
                entranceDelayMs={contentEntering ? 120 : 0}
              />
            ) : isExplore ? (
              <ExploreProfilesView
                currentProfileId={feedFocusId}
                onSelectProfile={openProfile}
                onClearProfile={clearSharedProfileFocus}
              />
            ) : isGallery ? (
              <BadgeGalleryView
                selectedProfileId={selectedProfileID}
                onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                onSelectProfile={openProfile}
                onClearProfile={() => onNavigate('badge_gallery')}
              />
            ) : isJourneyGallery ? (
              <JourneysGalleryView
                selectedProfileId={selectedProfileID}
                onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                onSelectProfile={openProfileJourneys}
                onClearProfile={() => onNavigate('journey_gallery')}
              />
            ) : isClaiming ? (
              <ClaimBadgesView
                profile={selectedProfile}
                setMode={setMode}
                onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                onSubmissionTriggered={onSubmissionTriggered}
                onSaveDisabledChange={onSaveDisabledChange}
                navigate={navigate}
                initialAutoClaimBadgeId={claimBadgePrime?.id ?? null}
                onInitialAutoClaimConsumed={onClaimBadgePrimeConsumed}
              />
            ) : isStats ? (
              <StatsView onOpenProfile={openProfileFromStats} setSelectedBadge={(badge) => setSelectedBadge?.(badge)} />
            ) : isBadgeHashes ? (
              <BadgeHashesView
                ownProfileId={ownProfileID}
                onSelectProfile={openProfile}
                onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                entranceDelayMs={contentEntering ? 120 : 0}
              />
            ) : isBattle ? (
              <BattleViewportGate>
                {isBattleHowTo ? (
                  <BattleHowToView
                    playerName={profile?.name ?? 'You'}
                    playerId={profile?.email ?? ''}
                    ownedBadgeIds={profile?.badgesIDs ?? []}
                    profileId={ownProfileID}
                    onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                  />
                ) : isBattleArena ? (
                  <BattleArenaView
                    playerName={profile?.name ?? 'You'}
                    playerId={profile?.email ?? ''}
                    ownedBadgeIds={profile?.badgesIDs ?? []}
                    profileId={ownProfileID}
                    onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                  />
                ) : isBattleLeaderboards ? (
                  <BattleLeaderboardsView ownProfileId={ownProfileID} entranceDelayMs={contentEntering ? 120 : 0} />
                ) : isBattleClassic ? (
                  <BadgesBattleView
                    playerName={profile?.name ?? 'You'}
                    playerId={profile?.email ?? ''}
                    profileId={ownProfileID}
                    ownedBadgeIds={profile?.badgesIDs ?? []}
                    onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                  />
                ) : null}
              </BattleViewportGate>
            ) : isProfileGallery ? (
              <ProfileCollectedView
                profile={selectedProfile}
                currentUserEmail={profile?.email ?? null}
                currentProfile={profile}
                mode={mode === 'submitted' || mode === 'journeys' || mode === 'profile_feed' ? mode : 'collected'}
                setMode={setMode}
                setSelectedProfileID={setSelectedProfileID}
                onSelectBadge={(badge) => setSelectedBadge?.(badge)}
                onClaimNewBadges={onClaimNewBadges}
                onSubmitBadgeIdea={onSubmitBadgeIdea}
                onSelectProfile={openProfileKeepingSection}
                onClearProfile={clearProfileSelection}
                navigate={navigate}
              />
            ) : null
          ) : null}
        </main>
      </div>
    </div>
  );
};
